import clonedeep from 'lodash/cloneDeep';
import { AnyAction } from 'redux';

import {
    authorizeEntityUpdateRequest,
    editEntityUpdateRequest,
    getEntityUpdateRequests,
    parkEntityUpdateRequest,
    rejectEntityUpdateRequest,
} from '../apiClient';
import { ActionCreators as AuthActions } from '../login/auth';
import Manager from '../managers/manager';
import { TransactionType } from '../transactions/transactions';
import { Filter } from '../utils/FilterTypes';
import NamedId from '../utils/NamedId';
import { throwErrorIfNotHandled } from '../utils/errorMiddleware';
import handleEntityUpdateConfirmationError from '../utils/handleEntityUpdateConfirmationError';
import { errorAction, successAction } from '../utils/notifications';
import { PagedState } from '../utils/paging';
import { SerializedBalanceEntry } from '../vendors/model';

export enum EntityType {
    beneficiaryDetails = 'beneficiaryDetails',
    beneficiarySingleBlock = 'beneficiarySingleBlock',
    beneficiarySingleUnblock = 'beneficiarySingleUnblock',
    beneficiaryZeroingBlock = 'beneficiaryZeroingBlock',
    beneficiaryZeroing = 'beneficiaryZeroing',
    alternativeCollectorStatusChange = 'alternativeCollectorStatusChange',
    vendorDetails = 'vendorDetails',
    managerDetails = 'managerDetails',
    preParkedBeneficiary = 'preParkedBeneficiary',
    vendorUserDetails = 'vendorUserDetails',
    partnerDetails = 'partnerDetails',
    partnerUserDetails = 'partnerUserDetails',
    permissionProfileDetails = 'permissionProfileDetails',
    productManagement = 'productManagement',
}

const ActionTypes = {
    transactionsLoaded: 'taskAuthorizations.transactionsLoaded',
    transactionRequestPosted: 'taskAuthorizations.transactionRequestPosted',
    transactionRequestRejected: 'taskAuthorizations.transactionRequestRejected',
    entityUpdateRequestsLoaded: 'taskAuthorizations.entityUpdateRequestsLoaded',
    entityUpdateRequestEdited: 'taskAuthorizations.entityUpdateRequestEdited',
    entityUpdateRequestParked: 'taskAuthorization.entityUpdateRequestParked',
    entityUpdateRequestPosted: 'taskAuthorization.entityUpdateRequestPosted',
    entityUpdateRequestSigned: 'taskAuthorization.entityUpdateRequestSigned',
    entityUpdateRequestRejected: 'taskAuthorization.entityUpdateRequestRejected',
    listTypeChanged: 'taskAuthorization.listTypeChanged',
    clearEntityUpdateRequests: 'taskAuthorization.clearEntityUpdateRequests',
};

export enum UpdateStateListType {
    Requested = 'Requests',
    Posted = 'Posted',
    Rejected = 'Rejected',
}

export type ListedEntitiesType = EntityType | CustomEntityUpdateListType;

export const ActionCreators = {
    clearUpdateRequests() {
        return (dispatch) => {
            dispatch({
                type: ActionTypes.clearEntityUpdateRequests,
            });
        };
    },
    editEntityUpdateRequest(id: string, newUpdates: EntityUpdate[]) {
        return (dispatch): Promise<void> =>
            editEntityUpdateRequest(id, newUpdates)
                .then((): void => {
                    dispatch({
                        type: ActionTypes.entityUpdateRequestEdited,
                        payload: { id, newUpdates },
                    });
                })
                .catch((err): void => {
                    switch (err.status) {
                        case 404:
                            dispatch(errorAction('This entity update does not exist'));
                            break;
                        case 403:
                            dispatch(errorAction("It's forbidden to edit this type of adjustment"));
                            break;
                        case 400:
                            dispatch(errorAction('No updates sent'));
                            break;
                        case 422:
                            dispatch(errorAction('Error in new updates'));
                            break;
                        default:
                            dispatch(errorAction('Unknown error'));
                    }
                });
    },
    loadEntityUpdateRequests(
        listedEntitiesType: ListedEntitiesType,
        listType: UpdateStateListType,
        page: number,
        limit: number,
        filters?: Filter[]
    ) {
        return (dispatch): Promise<void> => {
            return getEntityUpdateRequests(listedEntitiesType, listType, page, limit, filters)
                .then((response): void => {
                    dispatch({
                        type: ActionTypes.entityUpdateRequestsLoaded,
                        payload: response,
                    });
                })
                .catch((): void => {
                    dispatch(errorAction('Failed to load update requests'));
                });
        };
    },

    parkEntityUpdateRequest(entityUpdateRequest: EntityUpdateRequest, manager: Manager) {
        return (dispatch): Promise<void> => {
            return parkEntityUpdateRequest(entityUpdateRequest)
                .then((): void => {
                    dispatch({
                        type: ActionTypes.entityUpdateRequestParked,
                        payload: {
                            id: entityUpdateRequest.id,
                            manager: manager,
                        },
                    });
                    dispatch(AuthActions.getPendingActions());
                    dispatch(successAction(`Succesfully parked beneficiary with id=${entityUpdateRequest.entityId}`));
                })
                .catch((): void => {
                    return dispatch(errorAction(`Could not park beneficiary with id=${entityUpdateRequest.entityId}`));
                });
        };
    },
    confirmEntityUpdateRequest(entityUpdateRequest: EntityUpdateRequest, callback?: () => void) {
        return (dispatch): Promise<void> => {
            return authorizeEntityUpdateRequest(entityUpdateRequest)
                .then((): void => {
                    dispatch({
                        type: ActionTypes.entityUpdateRequestPosted,
                        payload: { id: entityUpdateRequest.id },
                    });
                    let message = `Successfully authorized entity update request ${entityUpdateRequest.id}`;
                    if (entityUpdateRequest.entityType === EntityType.managerDetails) {
                        message = `Successfully authorized entity update request ${entityUpdateRequest.id}. Changes will be applied after relogging.`;
                    }
                    dispatch(AuthActions.getPendingActions());
                    dispatch(successAction(message));
                })
                .then(() => {
                    callback?.();
                })
                .catch((err): void => handleEntityUpdateConfirmationError(err, entityUpdateRequest, dispatch));
        };
    },
    rejectEntityUpdateRequest(entityUpdateRequest: EntityUpdateRequest, comment: string, callback?: () => void) {
        return (dispatch): Promise<void> => {
            return rejectEntityUpdateRequest(entityUpdateRequest, comment)
                .then(() => {
                    dispatch({
                        type: ActionTypes.entityUpdateRequestRejected,
                        payload: { id: entityUpdateRequest.id },
                    });
                    dispatch(AuthActions.getPendingActions());
                    dispatch(successAction('Entity update request has been successfully rejected.'));
                })
                .then(() => {
                    callback?.();
                })
                .catch(throwErrorIfNotHandled('Failed to reject entity update request.'));
        };
    },
    changeListType(listType: UpdateStateListType) {
        return {
            type: ActionTypes.listTypeChanged,
            payload: listType,
        };
    },
};

export class TransactionRequest {
    id?: string;
    beneficiaryId?: string;
    vendor: NamedId;
    entitlements?: Array<SerializedBalanceEntry>;
    receiverId?: string;
    senderId?: string;
    type?: TransactionType;
    createdAt?: string;
    source?: string;
    reason?: string;
    authorization?: {
        id?: string;
        causedByTransactionId?: string;
        comment?: string;
        createdAt?: string;
        authorizedAt?: string;
        deletedAt?: string;
        authorizedByManager?: NamedId;
        createdByManager?: NamedId;
        deletedByManager?: NamedId;
    };
    originalTransaction?: {
        id?: number;
        category?: string;
        originalAmount?: number;
        currentAmount?: number;
        currency?: string;
        type?: string;
    };
}

export class EntityUpdate {
    constructor(
        public path: string,
        public newValue,
        public oldValue?
    ) {}
}

export class EntityUpdateReference {
    id: string;
    entityId: string;
    entityType: EntityType;
}

export enum CustomEntityUpdateListType {
    beneficiaryAllUpdates = 'beneficiaries',
}

export class EntityUpdateRequest {
    id?: string;
    title?: Array<string>;
    entityType?: EntityType;
    entityId?: string;
    updates?: EntityUpdate[];
    authorizedAt?: string;
    preParkedByPartnerId?: string;
    createdByManager?: NamedId;
    authorizedByManager?: NamedId;
    createdAt?: string;
    deletedAt?: string;
    deletedByManager?: NamedId;
    comment?: string;
    validFrom?: Date | string;
}

export class ProductUpdate {
    constructor(
        public path: string,
        public newValue: {
            minPrice: string;
            maxPrice: string;
            isFixed?: boolean;
        },
        public oldValue?: { minPrice: string; maxPrice: string; isFixed?: boolean }
    ) {}
}

export class ProductUpdateRequest extends EntityUpdateRequest {
    entityType: EntityType.productManagement;
    updates?: ProductUpdate[];
}

export class TaskAuthorizationsState {
    constructor(
        public transactionRequests: PagedState<TransactionRequest> = new PagedState(),
        public entityUpdateRequests: PagedState<EntityUpdateRequest | ProductUpdateRequest> = new PagedState(),
        public activeListType: UpdateStateListType = UpdateStateListType.Requested
    ) {}
}

function pagedStateWithoutTransactionRequestWithId(
    pagedState: PagedState<TransactionRequest>,
    transactionRequestId: string
) {
    const newState = clonedeep(pagedState) as PagedState<TransactionRequest>;
    newState.items = newState.items.filter((request) => request.id !== transactionRequestId);
    return newState;
}

function pagedStateWithoutEntityUpdateRequestWithId(
    pagedState: PagedState<EntityUpdateRequest>,
    entityUpdateRequestId: string
) {
    const newState = clonedeep(pagedState) as PagedState<EntityUpdateRequest>;
    newState.items = newState.items.filter((request) => request.id !== entityUpdateRequestId);
    return newState;
}

function pagedStateAfterParkingEntityUpdateRequestWithId(
    pagedState: PagedState<EntityUpdateRequest>,
    entityUpdateRequestId: string,
    manager: Manager
) {
    const newState = clonedeep(pagedState) as PagedState<EntityUpdateRequest>;
    newState.items = newState.items.map((item) => {
        if (item.id === entityUpdateRequestId) {
            const newItem = item;
            newItem.createdByManager = {
                id: manager.id,
                name: [manager.firstName, manager.lastName].join(' '),
                agency: manager.agency,
            };
            return newItem;
        } else {
            return item;
        }
    });
    return newState;
}

function pagedStateAfterEditingEntityUpdateRequestWithId(
    pagedState: PagedState<EntityUpdateRequest>,
    entityUpdateRequestId: string,
    newUpdates: EntityUpdate[]
) {
    const newState = clonedeep(pagedState) as PagedState<EntityUpdateRequest>;
    newState.items = newState.items.map((item) => {
        if (item.id === entityUpdateRequestId) {
            const newItem = item;
            newItem.updates = newUpdates;
            newItem.entityId = newUpdates.find((u) => u.path === 'id').newValue;
            return newItem;
        } else {
            return item;
        }
    });
    return newState;
}

export function taskAuthorizationsReducer(
    state: TaskAuthorizationsState = new TaskAuthorizationsState(),
    action: AnyAction
) {
    switch (action.type) {
        case ActionTypes.transactionsLoaded:
            return new TaskAuthorizationsState(action.payload, state.entityUpdateRequests, state.activeListType);
        case ActionTypes.transactionRequestPosted:
            const stateAfterTransactionPost = pagedStateWithoutTransactionRequestWithId(
                state.transactionRequests,
                action.payload.id
            );
            return new TaskAuthorizationsState(
                stateAfterTransactionPost,
                state.entityUpdateRequests,
                state.activeListType
            );
        case ActionTypes.transactionRequestRejected:
            const stateAfterTransactionRejection = pagedStateWithoutTransactionRequestWithId(
                state.transactionRequests,
                action.payload.id
            );
            return new TaskAuthorizationsState(
                stateAfterTransactionRejection,
                state.entityUpdateRequests,
                state.activeListType
            );
        case ActionTypes.entityUpdateRequestsLoaded:
            return new TaskAuthorizationsState(state.transactionRequests, action.payload, state.activeListType);
        case ActionTypes.entityUpdateRequestEdited:
            const stateAfterEntityUpdateRequestEditing = pagedStateAfterEditingEntityUpdateRequestWithId(
                state.entityUpdateRequests,
                action.payload.id,
                action.payload.newUpdates
            );
            return new TaskAuthorizationsState(
                state.transactionRequests,
                stateAfterEntityUpdateRequestEditing,
                state.activeListType
            );
        case ActionTypes.entityUpdateRequestParked:
            const stateAfterEntityUpdateRequestParking = pagedStateAfterParkingEntityUpdateRequestWithId(
                state.entityUpdateRequests,
                action.payload.id,
                action.payload.manager
            );
            return new TaskAuthorizationsState(
                state.transactionRequests,
                stateAfterEntityUpdateRequestParking,
                state.activeListType
            );
        case ActionTypes.entityUpdateRequestPosted:
            const stateAfterEntityUpdatePost = pagedStateWithoutEntityUpdateRequestWithId(
                state.entityUpdateRequests,
                action.payload.id
            );
            return new TaskAuthorizationsState(
                state.transactionRequests,
                stateAfterEntityUpdatePost,
                state.activeListType
            );
        case ActionTypes.entityUpdateRequestRejected:
            const stateAfterEntityUpdateRejection = pagedStateWithoutEntityUpdateRequestWithId(
                state.entityUpdateRequests,
                action.payload.id
            );
            return new TaskAuthorizationsState(
                state.transactionRequests,
                stateAfterEntityUpdateRejection,
                state.activeListType
            );
        case ActionTypes.listTypeChanged:
            return new TaskAuthorizationsState(state.transactionRequests, state.entityUpdateRequests, action.payload);
        case ActionTypes.clearEntityUpdateRequests:
            return new TaskAuthorizationsState();
    }
    return state;
}
